import React, { useState, useEffect, useRef } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationSchema } from "../../../service/validation/client";
import Input from "../../../components/FormInputs";
import UploadDocumentsModal from "../../Forms/Upload/Upload";
import FormTitle from "../../../components/FormTitle";
import AdminContainer from "../../Admin";
import Header from "../../../components/Header";
import PhoneInput from "../../../components/phoneinput";
import { OPERTIONS_URL } from "../../../common/common";
import SecureImage from "../../../components/BucketImage/Image";
import CompanyTypeSelector from "../Client/Company";
import toast from "react-hot-toast";
import { ApiService } from "../../../service/api.service";
import {
  FormValues,
  ExecutiveDetail,
} from "../../../service/interfaces/ApiResponse.interface";
import { Clientformtab } from "../../../common/tabs";
import CustomTabs from "../../../components/Tab";
import ExecutiveDetails from "./excutive";
import PaymentDetails from "../Payment/index";
import DocumentComponent from "../Client/Components/document";
import { useNavigate, useParams } from "react-router-dom";
import BillingDetails from "./Billing ";
import MapComponent from "../../../components/AuthMap";
import { client, Upload } from "../../../common/svg";

const ClientForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [uploadedImages, setUploadedImages] = useState<
    { url: string; name: string }[]
  >([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const apiService = new ApiService();
  const [useSameContactExecutive, setUseSameContactExecutive] = useState(false);
  const [useSameContactBilling, setUseSameContactBilling] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const entityId = localStorage.getItem("entity_id");
  const [activeTab, setActiveTab] = useState<string>("Executive Details");
  const [executiveDetailsData, setExecutiveDetailsData] = useState<any[]>([]);
  const [paymentDetailsData, setPaymentDetailsData] = useState<any[]>([]);
  const [billingDetailData, setBillingDetailData] = useState<any>({});
  const [billingAddressId, setBillingAddressId] = useState("");
  const [shippingDetailsData, setShippingDetailsData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [files, setFiles] = useState<File[]>([])
  const [apiError, setApiError] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([])
  const [errorsList, setErrorsList] = useState<string[]>([]);
  const [existingCompanyTypes, setExistingCompanyTypes] = useState<string[]>(
    []
  );
  const abortControllerRef = useRef<AbortController | null>(null);
  const addressDataRef = useRef<any>({});
  const methods = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      executiveDetails: [{
        firstName: "",
        lastName: "",
        role: "",
        phoneNumber: "",
        emailId: "",
        prefix: "",
        countryCode: "",
        dialCode: "",
        checkBox: 0
      }], // Initialize with one complete executive detail structure
    },
  });

  const {
    handleSubmit,
    trigger,
    reset,
    control,
    setValue,
    getValues,
    unregister,
    formState: { errors, isSubmitting },
  } = methods;

  const [formValues, setFormValues] = useState<FormValues>(() => getValues());

  useEffect(() => {
    const subscription = methods.watch((values) => {
      setFormValues({
        ...values,
        FirstName: values.FirstName || "",
        gstNumber: values.gstNumber || "",
        phoneNumber: values.phoneNumber || "",
        emailId: values.emailId || "",
        address1: values.address1 || "",
        address2: values.address2 || "",
        city: values.city || "",
        state: values.state || "",
        country: values.country || "",
        pincode: values.pincode || "",
        countryCode: values.countryCode || "",
        latitude: values.latitude || 0,
        longitude: values.longitude || 0,
        companyTypes: (values.companyTypes || []).filter(
          (type): type is string => !!type
        ),
        executiveDetails: (values.executiveDetails || []).filter(
          (detail): detail is ExecutiveDetail => !!detail
        ),
        paymentDetails: (values.paymentDetails || []).filter(
          (detail): detail is any => !!detail
        ),
      });
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  const getDataById = async () => {
    try {
      setIsLoading(true);
      setApiError(null);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      if (id) {
        setIsEditMode(true);
        const response: any = await apiService.getApi(
          `${OPERTIONS_URL}/client/details-route?clientId=${id}`
        );
        if (response && response.status === "200") {
          const clientData = response.body[0];
          if (clientData.contactFiles) {
            setUploadedFiles(clientData.contactFiles)
          }
          if (clientData.companyDetail) {
            setClientId(clientData.companyDetail.clientId);
            setValue("FirstName", clientData.companyDetail.firstName || "");
            setValue("gstNumber", clientData.companyDetail.gstNumber || "");
            setValue("phoneNumber", clientData.companyDetail.phoneNumber || "");
            setValue("emailId", clientData.companyDetail.emailId || "");
            setValue("address1", clientData.companyDetail.address1 || "");
            setValue("address2", clientData.companyDetail.address2 || "");
            setValue("pincode", clientData.companyDetail.pincode || "");
            setValue("city", clientData.companyDetail.city || "");
            setValue("state", clientData.companyDetail.state || "");
            setValue("country", clientData.companyDetail.country || "");
            setValue("countryCode", clientData.companyDetail.countryCode || "");
            setValue("latitude", clientData.companyDetail.latitude || "");
            setValue("longitude", clientData.companyDetail.longitude || "");

            addressDataRef.current = {
              address1: clientData.companyDetail.address1 || "",
              address2: clientData.companyDetail.address2 || "",
              city: clientData.companyDetail.city || "",
              state: clientData.companyDetail.state || "",
              country: clientData.companyDetail.country || "",
              pincode: clientData.companyDetail.pincode || "",
            };

            if (
              clientData.companyTypes &&
              Array.isArray(clientData.companyTypes)
            ) {
              setValue("companyTypes", clientData.companyTypes);
              setExistingCompanyTypes(clientData.companyTypes);
            }
          }

          if (
            clientData.executiveDetails &&
            Array.isArray(clientData.executiveDetails)
          ) {
            setExecutiveDetailsData(clientData.executiveDetails);
            setValue("executiveDetails", clientData.executiveDetails);
          }

          if (
            clientData.paymentDetails &&
            Array.isArray(clientData.paymentDetails)
          ) {
            setPaymentDetailsData(clientData.paymentDetails);
            setValue("paymentDetails", clientData.paymentDetails);
          }

          if (
            clientData.billingDetail &&
            typeof clientData.billingDetail === "object"
          ) {
            setBillingDetailData(clientData.billingDetail);
            setValue("billingAddress", clientData.billingDetail);
            setBillingAddressId(clientData.billingDetail?.billingAddressId);
          } else {
            setBillingDetailData({});
          }

          if (
            clientData.shippingDetails &&
            Array.isArray(clientData.shippingDetails)
          ) {
            setShippingDetailsData(clientData.shippingDetails);
            setValue("shippingDetails", clientData.shippingDetails);
          }

          if (clientData.documents && Array.isArray(clientData.documents)) {
            const formattedImages = clientData.documents.map((doc: any) => ({
              url: doc.url,
              name: doc.name || "Document",
            }));
            setUploadedImages(formattedImages);
          }
        } else {
        }
      }
    } catch (error: any) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        return;
      }
      setApiError(
        "Error loading client data. Please check your connection and try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getDataById();
    } else {
      reset();
      setExecutiveDetailsData([]);
      setPaymentDetailsData([]);
      setBillingDetailData([]);
      setShippingDetailsData([]);
      setUploadedImages([]);
      setIsEditMode(false);
      setApiError(null);

      addressDataRef.current = {};
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [id]);
  // useEffect(()=>{
  //   if(executiveDetailsData){
  //     console.log("test:", executiveDetailsData)
  //   }
  // },
  // [setExecutiveDetailsData,executiveDetailsData])
  useEffect(() => {
    const addressFields = [
      "address1",
      "address2",
      "city",
      "state",
      "country",
      "pincode",
    ];
    const hasAddressChanged = addressFields.some(
      (field) =>
        addressDataRef.current[field] !== formValues[field as keyof FormValues]
    );

    if (hasAddressChanged) {
      addressDataRef.current = {
        address1: formValues.address1 || "",
        address2: formValues.address2 || "",
        city: formValues.city || "",
        state: formValues.state || "",
        country: formValues.country || "",
        pincode: formValues.pincode || "",
      };
    }
  }, [
    formValues.address1,
    formValues.address2,
    formValues.city,
    formValues.state,
    formValues.country,
    formValues.pincode,
  ]);

  const handleLocationSelect = (location: {
    lat: number;
    lng: number;
    address?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    country?: string;
    pincode?: string;
    id?: string;
  }) => {
    if (location.address1) setValue("address1", location.address1);
    if (location.address2) setValue("address2", location.address2);
    if (location.city) setValue("city", location.city);
    if (location.state) setValue("state", location.state);
    if (location.country) setValue("country", location.country);
    if (location.pincode) setValue("pincode", location.pincode);

    setValue("latitude", location.lat);
    setValue("longitude", location.lng);
    addressDataRef.current = {
      address1: location.address1 || addressDataRef.current.address1 || "",
      address2: location.address2 || addressDataRef.current.address2 || "",
      city: location.city || addressDataRef.current.city || "",
      state: location.state || addressDataRef.current.state || "",
      country: location.country || addressDataRef.current.country || "",
      pincode: location.pincode || addressDataRef.current.pincode || "",
    };

    trigger(["address1", "city", "state", "country", "pincode"]);
  };
  const getAddressDataForMap = () => {
    return addressDataRef.current;
  };

  const onSubmit = async (data: FormValues) => {
    console.log("Form submitted with data:", data);
    console.log("Validation errors:", errors);
    setApiError(null);

    // Explicitly trigger validation for executive details
    await trigger("executiveDetails");

    try {
      await handleFormSubmit(data);
    } catch (error: any) {
      setApiError(error.message || "An error occurred during submission");
    }
  };
  const handleFormSubmit = async (data: FormValues) => {
    try {
      console.log("Form submission started with data:", data);
      console.log("Form errors:", errors);

      // Cancel previous requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const formData = new FormData();

      if (entityId) {
        formData.append("entityId", entityId);
      }

      // Company Detail
      const companyDetail = {
        firstName: data?.FirstName,
        emailId: data?.emailId,
        phoneNumber: data?.phoneNumber,
        address1: data?.address1,
        address2: data?.address2 || undefined,
        pincode: data?.pincode,
        city: data?.city,
        state: data?.state,
        country: data?.country,
        gstNumber: data?.gstNumber,
        countryCode: data?.countryCode,
        latitude: data?.latitude,
        longitude: data?.longitude,
        // Only add clientId if we have actual data and are in edit mode
      };

      const filteredCompanyDetail = Object.fromEntries(
        Object.entries(companyDetail).filter(([_, value]) =>
          value !== null && value !== undefined && value !== ""
        )
      );

      // Only add clientId if we have filtered data to submit
      if (Object.keys(filteredCompanyDetail).length > 0 && (id || clientId)) {
        filteredCompanyDetail.clientId = id ?? clientId ?? undefined;
      }

      if (Object.keys(filteredCompanyDetail).length > 0) {
        formData.append("companyDetail", JSON.stringify(filteredCompanyDetail));
      }

      // Company Types
      if (data?.companyTypes?.length) {
        const companyTypes = (isEditMode && id)
          ? data.companyTypes.map((type) => {
            const businessTypeId =
              typeof type === "object" && "businessTypeId" in type
                ? (type as { businessTypeId: string }).businessTypeId
                : type;

            // Only include clientId and entityId if they exist
            const typeObj: any = { businessTypeId };
            if (id) typeObj.clientId = id;
            if (entityId) typeObj.entityId = entityId;

            return typeObj;
          })
          : data.companyTypes;

        formData.append("companyTypes", JSON.stringify(companyTypes));
      }

      // Executive Details
      const executives = executiveDetailsData.map((exec) => {
        const filtered = Object.fromEntries(
          Object.entries(exec).filter(([_, value]) =>
            value !== null && value !== undefined && value !== ""
          )
        );

        // Only add clientId if we have filtered data to submit
        if (isEditMode && id && Object.keys(filtered).length > 0) {
          filtered.clientId = id;
        }

        delete filtered.executiveId;

        return filtered;
      }).filter(exec => Object.keys(exec).length > 0);

      if (executives.length > 0) {
        formData.append("executiveDetails", JSON.stringify(executives));
      }

      // Payment Details
      const paymentDetails = (data?.paymentDetails || paymentDetailsData || [])
        .map((payment) => {
          const filtered = Object.fromEntries(
            Object.entries(payment).filter(([_, val]) =>
              val !== null && val !== undefined && val !== ""
            )
          );

          // Only add clientId if we have data to submit
          if (Object.keys(filtered).length > 0 && id) {
            filtered.clientId = id;
          }

          return Object.keys(filtered).length > 0 ? filtered : null;
        })
        .filter(Boolean);

      if (paymentDetails.length > 0) {
        formData.append("paymentDetails", JSON.stringify(paymentDetails));
      }

      // Billing Detail
      if (billingDetailData) {
        const billingDetail = {
          ...billingDetailData,
          billingAddress:
            billingDetailData?.billingAddress &&
              typeof billingDetailData.billingAddress === "object"
              ? billingDetailData.billingAddress
              : undefined,
        };

        // Only add ids if we have data
        if (id) billingDetail.clientId = id;
        if (billingAddressId) billingDetail.billingAddressId = billingAddressId;

        const filteredBillingDetail = Object.fromEntries(
          Object.entries(billingDetail).filter(([_, value]) =>
            value !== null && value !== undefined && value !== ""
          )
        );

        if (Object.keys(filteredBillingDetail).length > 0) {
          formData.append("billingDetail", JSON.stringify(filteredBillingDetail));
        }
      }

      // Shipping Details
      const shippingDetails = (data?.shippingDetails || shippingDetailsData || [])
        .map((ship) => {
          const filtered = Object.fromEntries(
            Object.entries(ship).filter(([_, val]) =>
              val !== null && val !== undefined && val !== ""
            )
          );

          // Only add clientId if we have data to submit
          if (Object.keys(filtered).length > 0 && id) {
            filtered.clientId = id;
          }

          return Object.keys(filtered).length > 0 ? filtered : null;
        })
        .filter(Boolean);

      if (shippingDetails.length > 0) {
        formData.append("shippingDetails", JSON.stringify(shippingDetails));
      }

      // Existing Documents
      const existingDocuments = uploadedFiles
        ?.filter(file => file.fileId)
        .map(file => ({
          fileId: file.fileId,
          fileName: file.fileName,
          s3BucketName: file.s3BucketName,
          s3ObjectKey: file.s3ObjectKey,
        }));

      if (existingDocuments && existingDocuments.length > 0) {
        formData.append("contactFiles", JSON.stringify(existingDocuments));
      }

      // New Files
      if (files && files.length > 0) {
        files.forEach(file => {
          formData.append("files", file);
        });
      }

      // Submit the form
      console.log("Submitting form data...");
      let response;

      if (isEditMode && id) {
        response = await apiService.putApi(`${OPERTIONS_URL}/client/update-route`, formData);
      } else {
        response = await apiService.postApi(`${OPERTIONS_URL}/client/store-route`, formData);
      }

      console.log("API Response:", response);

      if (response && response.status?.toString() === "200") {
        toast.success("Client created successfully!");
        reset();
        navigate("/client", { replace: true });
        window.location.reload()
      } else {
        const errorMessage =
          response?.message || "Error submitting form. Please try again.";
        console.error("API Error:", errorMessage);
        toast.error(errorMessage);
        setApiError(errorMessage);
      }
    } catch (error: any) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        console.log("Request was canceled");
        return;
      }

      const errorMessage = error.message || "An error occurred while submitting the form. Please try again.";
      console.error("Form submission error:", error);
      toast.error(errorMessage);
      setApiError(errorMessage);
    }
  };


  const toggleModal = () => {
    setIsModalOpen((prev) => !prev);
  };

  const handleCancel = () => {
    if (isEditMode && id) {
      getDataById();
    } else {
      reset();
      setValue("companyTypes", []);
      setExecutiveDetailsData([]);
      setPaymentDetailsData([]);
      setBillingDetailData([]);
      setShippingDetailsData([]);
      setApiError(null);

      addressDataRef.current = {};
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2">Loading client data...</p>
        </div>
      </div>
    );
  }
  // console.log(files)

  return (
    <div>
      <div className="mb--3">
        <Header
          icon={client}
          title={
            isEditMode ? "Edit Vendor Profile" : "Add New Vendor Profile"
          }
          showSearch={false}
        >
          <div onClick={toggleModal} className="cursor-pointer">
            <SecureImage fileKey={Upload} alt="header icon" width="30px" />
          </div>
        </Header>
        {isModalOpen && (
          <UploadDocumentsModal
            uploadedImages={uploadedImages}
            setUploadedImages={setUploadedImages}
            files={files}
            setFiles={setFiles}
            toggleModal={toggleModal}
          />
        )}

      </div>

      <AdminContainer>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="bg-white p-6 rounded-[20px] font-poppins">
              <FormTitle title="Company Details" />
              <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-4 md:gap-x-[1.5rem]">
                <Input
                  label="Company Name"
                  name="FirstName"
                  errors={errors}
                  register={methods.register}
                  placeholder="Enter company name"
                  type="text"
                  onBlur={() => trigger("FirstName")}
                />
                <Input
                  label="GST Number"
                  name="gstNumber"
                  errors={errors}
                  register={methods.register}
                  placeholder="Enter GST Number"
                  maxLength={15}
                  type="text"
                  uppercase
                  onBlur={() => trigger("gstNumber")}
                />
                <PhoneInput<FormValues>
                  label="Phone Number"
                  name="phoneNumber"
                  defaultCountry="IN"
                  type="tel"
                  errors={errors}
                  maxLength={10}
                  placeholder="Enter your phone number"
                  control={control}
                  value={getValues("phoneNumber")}
                  defaultValue={{
                    countryCode: getValues("countryCode") || "",
                    phoneNumber: getValues("phoneNumber") || "",
                    dialCode: getValues("dialCode") || "",
                  }}
                  onChange={({ countryCode, phoneNumber, dialCode }) => {
                    setValue("phoneNumber", phoneNumber);
                    setValue("countryCode", countryCode);
                    //  setValue("dialCode", dialCode); // Uncomment if you need to set dialCode
                  }}
                />

                <Input
                  label="Mail ID"
                  name="emailId"
                  errors={errors}
                  register={methods.register}
                  placeholder="Enter Mail ID"
                  type="email"
                  onBlur={() => trigger("emailId")}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div>
                  <div className="grid md:grid-cols-2 gap-x-6 gap-y-4">
                    <Input
                      label="Address Line 1"
                      name="address1"
                      errors={errors}
                      register={methods.register}
                      placeholder="Enter address line 1"
                    // onBlur={() => trigger("address1")}
                    />
                    <Input
                      label="Pincode"
                      name="pincode"
                      errors={errors}
                      register={methods.register}
                      type="number"
                      placeholder="Enter Pincode"
                      maxLength={6}
                    // onBlur={() => {
                    //   trigger("pincode");
                    //   methods.trigger();
                    // }}
                    />
                  </div>

                  <div className="mt-4 grid md:grid-cols-2 gap-x-6 gap-y-4">
                    <Input
                      label="Address Line 2"
                      name="address2"
                      errors={errors}
                      register={methods.register}
                      placeholder="Enter address line 2"
                    // onBlur={() => {
                    //   trigger("address2");
                    //   methods.trigger();
                    // }}
                    />
                    <Input
                      label="State"
                      name="state"
                      errors={errors}
                      register={methods.register}
                      placeholder="Enter State"
                    // onBlur={() => {
                    //   trigger("state");
                    //   methods.trigger();
                    // }}
                    />
                  </div>

                  <div className="mt-4 grid md:grid-cols-2 gap-x-6 gap-y-4">
                    <Input
                      label="City"
                      name="city"
                      errors={errors}
                      register={methods.register}
                      placeholder="Enter City"
                    // onBlur={() => {
                    //   trigger("city");
                    //   methods.trigger();
                    // }}
                    />
                    <Input
                      label="Country"
                      name="country"
                      errors={errors}
                      register={methods.register}
                      placeholder="Enter Country"
                    // onBlur={() => {
                    //   trigger("country");
                    //   methods.trigger();
                    // }}
                    />
                  </div>

                  {/* Hidden fields for latitude and longitude */}
                  {/* Replace the existing hidden input fields with this code */}
                  <input
                    type="hidden"
                    {...methods.register("latitude", {
                      setValueAs: (value) => value === "" ? 0 : parseFloat(value)
                    })}
                    defaultValue={formValues.latitude || 0}
                  />
                  <input
                    type="hidden"
                    {...methods.register("longitude", {
                      setValueAs: (value) => value === "" ? 0 : parseFloat(value)
                    })}
                    defaultValue={formValues.longitude || 0}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location on Map
                  </label>
                  <MapComponent
                    onLocationSelect={handleLocationSelect}
                    addressData={getAddressDataForMap()}
                  />
                </div>
              </div>
            </div>
            <br />
            <div className="p-2 bg-white rounded-[20px]">
              {isEditMode ? (
                <CompanyTypeSelector
                  errors={errors}
                  setValue={setValue}
                  defaultValues={existingCompanyTypes}
                  isEditMode={true}
                />
              ) : (
                <CompanyTypeSelector errors={errors} setValue={setValue} />
              )}
            </div>
            <br />
            <CustomTabs tabs={Clientformtab} onTabChange={setActiveTab} />
            <br />
            {activeTab === "Executive Details" && (
              <ExecutiveDetails
                setExecutiveData={setExecutiveDetailsData}
                useSameContact={useSameContactExecutive}
                setUseSameContact={setUseSameContactExecutive}
                register={methods.register}
                errors={errors}
                parentUnregister={unregister}
                control={control}
                clientPhoneNumber={formValues.phoneNumber}
                clientEmailId={formValues.emailId}
                clientCountryCode={formValues.countryCode}
                executiveDetailsProp={executiveDetailsData}
              />
            )}
            {activeTab === "Billing Details" && (
              <BillingDetails
                setBillingDetailData={setBillingDetailData}
                clientPhoneNumber={formValues.phoneNumber}
                clientEmailId={formValues.emailId}
                clientAddress1={formValues.address1}
                clientAddress2={formValues.address2}
                clientCity={formValues.city}
                clientState={formValues.state}
                clientCountry={formValues.country}
                clientPincode={formValues.pincode}
                clientCountryCode={formValues.countryCode}
                setValue={methods.setValue}
                setShippingDetailsData={setShippingDetailsData}
                control={control}
                register={methods.register}
                errors={errors}
                initialShippingDetails={shippingDetailsData}
                unregister={unregister}
                useSameContact={useSameContactBilling}
                setUseSameContact={setUseSameContactBilling}
              />
            )}
            {activeTab === "Payment Details" && (
              <PaymentDetails
                setPaymentDetailsData={setPaymentDetailsData}
                register={methods.register}
                errors={errors}
                initialPaymentDetails={paymentDetailsData}
                parentUnregister={unregister}
              />
            )}

            {activeTab === "Document" && (
              <DocumentComponent uploadedFiles={uploadedFiles} setUploadedFiles={setUploadedFiles} setUploadedImages={setUploadedImages} uploadedImages={uploadedImages} setFiles={setFiles} files={files} />
            )}
            {errorsList.length > 0 && (
              <div className="flex items-center justify-between bg-red-50 border-l-4 border-red-500 p-4 rounded-lg shadow-md mb-6">
                <div className="flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 text-red-500 mr-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <div>
                    <h4 className="text-lg font-semibold text-red-700 mb-2">
                      Required Fields
                    </h4>
                    <ul className="list-disc list-inside text-red-600 space-y-1">
                      {errorsList.map((error, index) => (
                        <li key={index} className="text-sm">{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-end mt-6 gap-4">
              <button
                type="button"
                className="w-36 h-10 rounded-lg text-[#444FBD] border border-[#444FBD] hover:border-gray-400 transition duration-200"
                onClick={() => {
                  handleCancel();
                  navigate("/client");
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-36 h-10 rounded-lg bg-[#444FBD] text-white transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </form>
        </FormProvider>
      </AdminContainer>
    </div>
  );
};

export default ClientForm;
